import { z } from 'zod';
import { instantSchema, limitModeSchema, uuidSchema } from '../schema/rows.js';
import { DomainError } from './types.js';
/**
 * «Opciones de vista» (VIS-01, VIS-03, design D6 of f1-asap-view) and the acknowledgement of
 * rejected changes (SYN-05, D5). Each command sends only the columns that change; the options of
 * a view are one JSON column, merged with what it had so unknown keys survive.
 */
export const completedOptionsSchema = z.object({
    display: z.enum(['strike', 'section', 'hide']),
    window: z.enum(['period', 'today', 'always']),
});
export const viewOptionsSchema = z
    .object({
    completed: completedOptionsSchema.optional(),
    progress: z.object({ show: z.boolean() }).optional(),
    highlights: z.object({ enabled: z.boolean(), type_key: z.string().min(1) }).optional(),
})
    .loose();
/** Orders offered by the panel (maqueta 02); each is the `sort` the template writes. */
export const SORT_PRESETS = {
    due_then_manual: [
        { field: 'due_on', dir: 'asc', nulls: 'last' },
        { field: 'sort_key', dir: 'asc' },
    ],
    manual: [{ field: 'sort_key', dir: 'asc' }],
    start_then_newest: [
        { field: 'start_on', dir: 'asc', nulls: 'last' },
        { field: 'created_at', dir: 'desc' },
    ],
    newest: [{ field: 'created_at', dir: 'desc' }],
    follow_up: [{ field: 'field:follow_up_on', dir: 'asc', nulls: 'last' }],
};
const sortPresetSchema = z.enum([
    'due_then_manual',
    'manual',
    'start_then_newest',
    'newest',
    'follow_up',
]);
/** What «Agrupar por» and «Ordenar por» offer in each view (design D6 of f1-deferred-views). */
const VIEW_CHOICES = {
    asap: { groupBy: 'tag:context', sorts: ['due_then_manual', 'manual'] },
    backlog: { groupBy: 'tag:area', sorts: ['start_then_newest', 'newest', 'manual'] },
    someday: { groupBy: 'tag:area', sorts: ['newest', 'manual'] },
    waiting: { groupBy: 'field:waiting_on', sorts: ['follow_up', 'newest'] },
};
const DEFAULT_CHOICES = VIEW_CHOICES.asap;
export function viewChoices(viewKey) {
    return VIEW_CHOICES[viewKey] ?? DEFAULT_CHOICES;
}
/** The preset a saved `sort` matches, if any. */
export function sortPresetOf(sort) {
    const saved = JSON.stringify(sort);
    const match = Object.keys(SORT_PRESETS).find((key) => JSON.stringify(SORT_PRESETS[key]) === saved);
    return match ?? null;
}
const updateViewOptionsInput = z.object({
    view: z.object({
        id: uuidSchema,
        /** The view's key: it decides the groupings and orders it offers. */
        key: z.string().min(1),
        group_by: z.string().nullable(),
        sort: z.unknown(),
        options: z.record(z.string(), z.unknown()),
    }),
    patch: z.object({
        group_by: z.string().min(1).nullable().optional(),
        sort: sortPresetSchema.optional(),
        completed: completedOptionsSchema.partial().optional(),
        progress: z.object({ show: z.boolean() }).optional(),
        highlights_enabled: z.boolean().optional(),
    }),
});
const sameJson = (a, b) => JSON.stringify(a) === JSON.stringify(b);
export function updateViewOptions(input) {
    const { view, patch } = updateViewOptionsInput.parse(input);
    const choices = viewChoices(view.key);
    if ((patch.group_by !== undefined &&
        patch.group_by !== null &&
        patch.group_by !== choices.groupBy) ||
        (patch.sort !== undefined && !choices.sorts.includes(patch.sort))) {
        throw new DomainError('invalid_input', 'Esta vista no ofrece esa agrupación u orden.');
    }
    const data = {};
    if (patch.group_by !== undefined && patch.group_by !== view.group_by) {
        data.group_by = patch.group_by;
    }
    if (patch.sort !== undefined) {
        const sort = SORT_PRESETS[patch.sort];
        if (!sameJson(sort, view.sort))
            data.sort = sort;
    }
    const current = viewOptionsSchema.parse(view.options);
    const next = { ...current };
    if (patch.completed) {
        next.completed = completedOptionsSchema.parse({
            display: 'section',
            window: 'period',
            ...current.completed,
            ...patch.completed,
        });
    }
    if (patch.progress)
        next.progress = patch.progress;
    if (patch.highlights_enabled !== undefined) {
        next.highlights = {
            type_key: current.highlights?.type_key ?? 'tai',
            enabled: patch.highlights_enabled,
        };
    }
    if (!sameJson(next, current))
        data.options = next;
    if (Object.keys(data).length === 0)
        return [];
    return [{ table: 'views', op: 'PATCH', id: view.id, data }];
}
const updateHighlightTypeInput = z.object({
    type: z.object({
        id: uuidSchema,
        max_per_period: z.number().int().nullable(),
        limit_mode: limitModeSchema,
        pin_top: z.union([z.boolean(), z.literal(0), z.literal(1)]),
    }),
    patch: z.object({
        /** A positive number, or null for «Sin límite». */
        max_per_period: z.number().int().min(1).max(99).nullable().optional(),
        limit_mode: limitModeSchema.optional(),
        pin_top: z.boolean().optional(),
    }),
});
/** Limit, limit mode and pinning of the user's highlight type (TAI-03, TAI-04). */
export function updateHighlightType(input) {
    const parsed = updateHighlightTypeInput.safeParse(input);
    if (!parsed.success) {
        throw new DomainError('invalid_input', 'El máximo por periodo va de 1 a 99 o sin límite.');
    }
    const { type, patch } = parsed.data;
    const data = {};
    if (patch.max_per_period !== undefined && patch.max_per_period !== type.max_per_period) {
        data.max_per_period = patch.max_per_period;
    }
    if (patch.limit_mode !== undefined && patch.limit_mode !== type.limit_mode) {
        data.limit_mode = patch.limit_mode;
    }
    if (patch.pin_top !== undefined && patch.pin_top !== Boolean(type.pin_top)) {
        data.pin_top = patch.pin_top ? 1 : 0;
    }
    if (Object.keys(data).length === 0)
        return [];
    return [{ table: 'highlight_types', op: 'PATCH', id: type.id, data }];
}
const acknowledgeRejectionInput = z.object({ id: uuidSchema, now: instantSchema.optional() });
/** «Entendido» on a rejected change (SYN-05): seen on every device of the user. */
export function acknowledgeRejection(input) {
    const { id, now } = acknowledgeRejectionInput.parse(input);
    return [
        {
            table: 'sync_rejections',
            op: 'PATCH',
            id: id,
            data: { acknowledged_at: now ?? new Date().toISOString() },
        },
    ];
}
//# sourceMappingURL=views.js.map